import { apiFetch } from "../utils/apiUtils";

// Types matching the mobile app
export interface Notification {
    id: number;
    notification_type: string;
    title: string;
    message: string;
    /**
     * The ids of what the notification is about (a work item, say) — the same
     * `data` the push payload carries, for the same reason: opening the thing
     * needs an id, not the words. Optional because an api that predates it
     * sends nothing, and many types carry none.
     */
    data?: Record<string, unknown>;
    read_at: string | null;
    created_at: string;
}

export interface NotificationsResponse {
    notifications: Notification[];
    total_count: number;
    unread_count: number;
    has_more: boolean;
}

export interface NotificationFilters {
    page?: number;
    limit?: number;
    unread_only?: boolean;
}

export class NotificationService {
    private static instance: NotificationService;

    public static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    /**
     * Get notifications with pagination and filtering
     */
    async getNotifications(filters: NotificationFilters = {}): Promise<NotificationsResponse> {
        const { page = 1, limit = 50, unread_only = false } = filters;

        try {
            const unreadParam = unread_only ? "&unread_only=true" : "";
            // The api pages by `per_page`, and ignored the `limit` this sent
            // alone: every call came back 50 rows, the sidebar's one-row count
            // included. `limit` stays for anything else reading it.
            const response = await apiFetch(
                `/api/v1/notifications?page=${page}&limit=${limit}&per_page=${limit}${unreadParam}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return {
                notifications: data.notifications ?? [],
                total_count: data.total_count ?? 0,
                unread_count: data.unread_count ?? 0,
                has_more: data.has_more ?? false,
            };
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            return this.getEmptyResponse();
        }
    }

    /**
     * Mark a single notification as read
     */
    async markAsRead(notificationId: number): Promise<boolean> {
        try {
            const response = await apiFetch(
                `/api/v1/notifications/${notificationId}/mark_as_read`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
            return false;
        }
    }

    /**
     * Mark all notifications as read
     */
    async markAllAsRead(): Promise<boolean> {
        try {
            const response = await apiFetch(
                "/api/v1/notifications/mark_all_as_read",
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error("Failed to mark all notifications as read:", error);
            return false;
        }
    }

    private getEmptyResponse(): NotificationsResponse {
        return {
            notifications: [],
            total_count: 0,
            unread_count: 0,
            has_more: false,
        };
    }
}

export const notificationService = NotificationService.getInstance();
